import React, { useMemo, useCallback, useEffect, useState } from "react";
import { PdfFormatLabel } from '@/common/pdf-restrictions';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, message, Typography, Dropdown } from "antd";
import CommonTable from "@/common/table";
import EllipsisText from "@/common/ellipsis-text";
import {
  useDownloadDocumentFileMutation,
  dashboardApi,
} from "@/pages/dashboard/dashboard.reducer";
import useFavouritesManager from "@/hooks/useFavouritesManager";
import {
  setValue,
  selectCurrentDashboardFields,
  setCurrentItem,
  resetColumns
} from "@/pages/dashboard/dashboard.slice";
import { showPanel, clearFilter } from "@/pages/shared/shared.reducer";
import {
  haveAbilityOpenDocuments,
  haveAbilityToDownloadSource,
  haveAbilityDownloadPdf,
} from "@/pages/login/permission-service";
import {
  clearContactDetails,
  getDetailsByUserId,
} from "@/common/user-profile/user-profile-reducer";
import StatusTag from "@/common/status-tag";
import WithAvatar from "@/common/with-avatar";
import panels from "@/constants/panels";
import get from "lodash.get";
import CustomContextMenu from "@/common/custom-context-menu";
import Copy from "clipboard-copy";
import { BASE_USER, BASE_CHECKER } from "@/constants/roles";
import NativeDownloadButton from "@/common/native-download-button";
import {
  getDashboardFormatsColumnWidth,
} from "@/common/table-format-column-utils";
import { getDashboardEmptyStateKind } from "@/pages/dashboard/index.utils";
import RowActionPopover from "@/common/row-action-popover";
import { openErrorNotification } from '@/notification';

const { Text } = Typography;

const DashboardTableView = ({
  showTotalCount,
  isResizable,
  isReorderable,
  id,
  permissions,
  tableData,
  refetch,
  resetSearch,
  showPanel,
  showCommentPanel,
  showProfilePanel,
  showPreviewPanel,
  getTableHeight,
  showSiteColumn,
  showAllRevisions,
  isLoading,
  tableCount,
  allItemsCount,
  updateTotalDocumentCount,
  sharedFilters,
  isFavouriteTab,
  width,
  pageSize,
  currentPage,
  queryFilters,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentDashboard, columns } = useSelector(
    selectCurrentDashboardFields
  );
  const siteId = useSelector((state) => state.dashboardPage.currentSiteId);
  const maxUserRole = useSelector((state) => state.userDetails.maxUserRole);
  const favouriteCount = useSelector((state) => state.dashboardPage.documentCounts.favourites);
  const [openActionRow, setOpenActionRow] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [downloadDocumentFile] = useDownloadDocumentFileMutation();

  const handleResetColumnsClick = useCallback(() => {
    const largeKey = 'dashboardTable.columns.large';
    const storageKey = largeKey;
    localStorage.removeItem(storageKey)
    try {
      dispatch(resetColumns(storageKey));
      messageApi.open({ type: 'success', content: 'Column layout reset.', duration: 2 });
    } catch (error) {
      openErrorNotification(`Failed to reset columns, please refresh the page: (${error.message})` );
      messageApi.open({ type: 'error', content: "Failed to reset columns, reset page", duration: 2 });
      setTimeout(() => window.location.reload(), 400);
    }
  }, [messageApi, dispatch]);

  const columnMenuItems = useMemo(() => [
    {
      key: 'reset-columns',
      label: 
        <div className='menu-item display-center'>
           <i className="ri-refresh-line" />
          <div style={{ marginLeft: "4px" }}>Reset columns to default</div>
        </div>,
      onClick: handleResetColumnsClick,
    },
  ], [handleResetColumnsClick]);

  const {
    toggleFavourite,
  } = useFavouritesManager({
    tableId: id,
    tableFilters: queryFilters || sharedFilters,
    updateTotalDocumentCount,
    refetch,
    siteId,
    updateOnlyFavourites: true,
  });

  const fetchAll = async () => {
    return await dispatch(
      dashboardApi.endpoints.getTableData.initiate({
        ...sharedFilters,
        pageSize: tableCount,
        currentPage: 1,
        favouritesOnly: isFavouriteTab || false,
      })
    );
  };

  const copyToClipboard = useCallback(
    (number) => {
      try {
        Copy(number);
        messageApi.open({
          type: "success",
          duration: 2,
          content: "Copied to clipboard",
        });
      } catch {
        messageApi.open({
          type: "error",
          duration: 2,
          content: "Could not copy to clipboard",
        });
      }
    },
    [messageApi]
  );

  const handleFavouriteClick = useCallback(
    async (item) => {
      try {
        await toggleFavourite(item);
      } catch (error) {
        console.error("Failed to toggle favourite:", error);
      }
    },
    [toggleFavourite]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilter(id));
    resetSearch();
  }, [dispatch, resetSearch, id]);

  useEffect(() => {
    if (!tableData && !isLoading) {
      refetch();
    }
  }, [isLoading, refetch, tableData]);

  const handleDownloadClick = useCallback(
    (documentFileId, documentTypeId, fileName, revision, documentNumber) => {
      downloadDocumentFile({
        documentFileIds: [documentFileId],
        documentTypeId,
        fileName,
        revision,
        documentNumber,
      });
    },
    [downloadDocumentFile]
  );

  const formatBytes = useMemo(
    () =>
      (bytes, decimals = 0) => {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const dm = Math.max(0, decimals);
        const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return (
          parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
        );
      },
    []
  );

  const emptyTableMessage = useCallback(() => {
    if (["documentDashboard", "allSitesDashboard"].includes(currentDashboard)) {
      const filters = queryFilters || sharedFilters;
      const hasActiveSearch = [filters?.documentNumber, filters?.vendorNumber, filters?.keywords]
        .some((value) => typeof value === "string" && value.trim().length > 0);
      const emptyKind = getDashboardEmptyStateKind({
        currentDashboard,
        isFavouriteTab,
        favouriteCount,
        hasActiveSearch,
        allItemsCount,
      });

      if (emptyKind === "no-favourites") {
        return (
          <div className="empty-table-message">
            <div className="empty-table-message-content">
              <p>No favourites yet. Add a document to your favourites to see it here.</p>
            </div>
          </div>
        );
      }
      if (emptyKind === "filtered") {
        return (
          <div className="empty-table-message">
            <div className="empty-table-message-content">
              <p>No data found for these search filter options.</p>
              <Button onClick={handleClearFilters}>Clear Search</Button>
            </div>
          </div>
        );
      }
      if (emptyKind === "no-documents") {
        return (
          <div className="empty-table-message empty-site-message" style={{ height: "100%"}}>
            <p>No Documents Uploaded</p>
            {maxUserRole && ![BASE_USER, BASE_CHECKER].includes(maxUserRole) && (
              <Button
                type="primary"
                icon={<i className="ri-upload-line" aria-hidden="true" />}
                onClick={() => navigate("/upload-document")}
              >
                Upload Documents
              </Button>
            )}
          </div>
        );
      }
    }
    return null;
  }, [
    currentDashboard,
    allItemsCount,
    favouriteCount,
    isFavouriteTab,
    queryFilters,
    sharedFilters,
    handleClearFilters,
    maxUserRole,
    navigate,
  ]);
  
  const handleFavouriteToggle = useCallback(
    (item) => (e) => {
      e.stopPropagation();
      return handleFavouriteClick(item);
    },
    [handleFavouriteClick]
  );

  const renderTitle = (title, canReorder = false) => (
    <Text className={`column-title${canReorder ? " dragHandler" : ""}`} ellipsis={{ tooltip: title }}>
      {title}
    </Text>
  );

  const renderDescriptions = useCallback(
    (item) => {
      const descriptions = [
        item.description1,
        item.description2,
        item.description3,
        item.description4,
        item.description5,
        item.description6,
      ].filter((desc) => desc);
      if (descriptions.length === 0) return null;
      const descriptionNodes = descriptions.map((desc, index) =>
        <EllipsisText 
          forceCapitalize={true} 
          key={index} 
          className="text-force-uppercase">
            {desc}
        </EllipsisText>
      );
      return (
        <div className="text-force-uppercase description-content">
          {descriptionNodes}
        </div>
      );
    },
    []
  );

  const renderFormatButtonGroup = useCallback(
    (item, options = {}) => {
      const {
        columnLayout = false,
        includeCommentButton = false,
        afterClick = () => {},
      } = options;

      if (!item || !item.documentFile) return null;
      const {
        publishedFileLocked,
        documentFileId,
        sourceFileName,
        publishedFileName,
        revisionNumber,
        publishedFileSize,
      } = item.documentFile;

      const canDownloadOriginal = haveAbilityToDownloadSource(permissions, item);
      const canDownloadPDF = haveAbilityDownloadPdf(permissions, item);

      const handleOriginalClick = () => {
        handleDownloadClick(
          documentFileId,
          1,
          sourceFileName,
          revisionNumber,
          item.documentNumber
        );
        afterClick();
      };

      const handlePdfClick = (e) => {
        e.stopPropagation();
        handleDownloadClick(
          documentFileId,
          2,
          publishedFileName,
          revisionNumber,
          item.documentNumber
        );
        afterClick();
      };

      const handlePreviewClick = (e) => {
        e.stopPropagation();
        showPreviewPanel(item);
        afterClick();
      };

      return (
        <div
          className="display-center gap-8"
          style={columnLayout ? { 
            flexDirection: "column",
            alignItems: "flex-start",
            height: "100px",
          } : { 
            flexDirection: "row" 
          }}
        >
          {includeCommentButton && item.underChangeComment && (
            <Button
              className="common-button display-center"
              onClick={(e) => {
                e.stopPropagation();
                showCommentPanel(item);
                afterClick();
              }}
              icon={<i className="ri-chat-4-line" />}
            >
              <i className="ri-chat-4-line" />
              Show Comment
            </Button>
          )}
          <NativeDownloadButton
            sourceFileName={sourceFileName}
            hasPermission={canDownloadOriginal}
            onDownload={handleOriginalClick}
            hasOriginalForOtherRevision={item.hasOriginal}
            size="small"
          />
          <Button
            disabled={!publishedFileName || !canDownloadPDF}
            onClick={handlePdfClick}
            title={`PDF ${formatBytes(publishedFileSize)}`}
            className="common-button display-center"
          >
            <PdfFormatLabel documentFileId={documentFileId} passwordRequired={publishedFileLocked} label={<div className="pdf-label">PDF</div>} />
          </Button>
          <Button
            disabled={!publishedFileName || !canDownloadPDF}
            className="common-button display-center"
            onClick={handlePreviewClick}
            title="PDF Preview"
          >
            {publishedFileLocked ? (
              <i className="lock-icon ri-lock-line" />
            ) : (
              <i className="preview-icon ri-eye-line" />
            )}
            <span className="m-0">Preview</span>
          </Button>
        </div>
      );
    },
  [permissions, handleDownloadClick, showPreviewPanel, showCommentPanel, formatBytes]
  );

  const buildColumns = useMemo(() => {
    const additionalColumns = [
      {
        title: renderTitle("Document/Drawing Number"),
        className: "document-number-cell",
        dataIndex: "documentNumber",
        fixed: width > 768 ? "left" : false,
        key: "documentNumber",
        isReorderableColumn: false,
        render: (number, item) => {
          const { documentFile } = item;

          const {
            documentFileId,
          } = documentFile;

          const canOpenDocuments = haveAbilityOpenDocuments(permissions, item);

          return (
            <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{ width: "calc(100% - 30px)" }}
                className="content-wrapper"
              >
                <div className={canOpenDocuments ? "table-action-link" : ""}>
                  <CustomContextMenu
                    canOpenDocuments={canOpenDocuments}
                    number={number}
                    item={item}
                    onCopy={(e) => {
                      e.stopPropagation();
                      copyToClipboard(number);
                    }}
                    onOpenPanel={() => showPanel(item)}
                  >
                    <EllipsisText
                      forceCapitalize={true}
                      className={
                        canOpenDocuments
                          ? "body-large-medium action-link"
                          : "body-large-medium"
                      }
                      onClick={(e) => {
                        if (canOpenDocuments) {
                          e.stopPropagation();
                          showPanel(item);
                        }
                      }}
                    >
                      {number}
                    </EllipsisText>
                  </CustomContextMenu>
                  {item.vendorNumber && (
                    <EllipsisText>Vend #: {item.vendorNumber}</EllipsisText>
                  )}
                  {item.hasAttachment && (
                    <div className="body-small-roman">
                      <div className="ri-attachment-line">
                        {item.attachmentCount} Attached file(s).
                      </div>
                    </div>
                  )}
                </div>
                <RowActionPopover
                  width={width}
                  rowId={documentFileId}
                  openRowId={openActionRow}
                  setOpenRowId={setOpenActionRow}
                  content={renderFormatButtonGroup(item, {
                    columnLayout: width < 768,
                    includeCommentButton: !!item.underChangeComment,
                    afterClick: () => setOpenActionRow(null),
                  })}
                />
              </div>
            </div>
              {currentDashboard !== "allSitesDashboard" && (
                <div className="favourite-icon-wrapper">
                <Button
                  title={
                    item.favourite
                      ? "Remove from favourites"
                      : "Add to favourites"
                  }
                  className={`favourite-icon ${
                    item.favourite ? "favourite" : "not-favourite"
                  }`}
                  onClick={handleFavouriteToggle(item)}
                >
                  <i className={`ri-star-${item.favourite ? 'fill' : 'line'}`} />
                </Button>
                </div>
              )}
            </>
          );
        },
      },
    ];

    if (showSiteColumn) {
      additionalColumns.push({
        title: renderTitle("Site", isReorderable),
        dataIndex: "siteName",
        className: "site-cell",
        key: "siteName",
        isEnabled: true,
        isReorderableColumn: true,
        render: (value) => <EllipsisText>{value}</EllipsisText>,
      });
    }

    additionalColumns.push(
      {
        title: renderTitle("Rev", isReorderable),
        dataIndex: "documentFile.revisionNumber",
        className: "rev-cell",
        key: "rev",
        isReorderableColumn: isReorderable,
        render: (_, item) => {
          const notCurrentRevision =
            showAllRevisions && item.documentFile.documentFileStatusId === 2;
          return (
            <div className={notCurrentRevision ? "chip warning" : ""}>
              <span>{item.documentFile.revisionNumber}</span>
            </div>
          );
        },
      },
      {
        title: renderTitle("Description", isReorderable),
        className: "description-cell",
        dataIndex: "description1",
        key: "description",
        isReorderableColumn: isReorderable,
        render: (_, item) => renderDescriptions(item),
      },
      {
        title: renderTitle("Status", isReorderable),
        dataIndex: "documentRevisionStatus",
        className: "document-status",
        key: "documentStatus",
        isReorderableColumn: isReorderable,
        render: (value, item) => {
          const isUnderChange = item.underChangeComment
          const isCurrent = item.documentFile.documentFileStatusId == 1;
          return (
            <div className={"description-content"}>
              {isCurrent && isUnderChange && 
              <div className="ellipsis-column mb-2">
                {item.underChangeUser && (
                  <div className="changedBy display-center table-action-link">
                    <WithAvatar
                      className={item.underChangeUser?.active ? "action-link" : "text-red"}
                      name={item.underChangeUser.name}
                      handleClick={(e) => {
                        e.stopPropagation();
                        showProfilePanel(item.underChangeUser.id);
                      }}
                    />
                  </div>
                )}
              </div>}

              <StatusTag type={!isCurrent ? "Superseded" : value} />
              {isCurrent && isUnderChange && 
              <>
                {item.latestApprovalPending != null && (
                  <div>
                    <EllipsisText className="text-red">
                      <b>
                        Revision {item.latestApprovalPending} is awaiting approval
                      </b>
                    </EllipsisText>
                  </div>
                )}
                <div className="ellipsis-column mt-2">
                  {item.underChangeComment && (
                    <Button
                      className="show-comment-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        showCommentPanel(item);
                      }}
                    >
                      Show Comment
                    </Button>
                  )}
                </div>
              </>}
            </div>
          );
        },
      },
      {
        title: renderTitle("Category / Sub Category", isReorderable),
        className: "category-cell",
        dataIndex: "category",
        key: "category",
        isReorderableColumn: isReorderable,
        render: (_, item) => (
          <span className="text-force-uppercase">
            {item.category} / {item.subCategory}
          </span>
        ),
      },
      {
        title: renderTitle("Available Formats"),
        className: "actions-row text-center dashboard-formats-column",
        key: "availableFormats",
        dataIndex: 'fixed-hover-right',
        isReorderableColumn: false,
        render: (_, item) => {
          const { documentFile } = item;
          if (documentFile) {
            return renderFormatButtonGroup(item, {
              columnLayout: width > 768 && width < 1580,
              includeCommentButton: false,
            });
          }
          return null;
        },
      },
    );
    const columnsWithWidth = isResizable
      ? additionalColumns.map((item) => {
          const matchingItem = columns.find(
            (x) => x.dataIndex === item.dataIndex
          );
          return matchingItem
            ? {
                ...item,
                width: item.dataIndex === 'fixed-hover-right'
                  ? getDashboardFormatsColumnWidth(width, matchingItem.width)
                  : matchingItem.width,
                icon: matchingItem.icon,
                label: matchingItem.label,
                isEnabled: matchingItem.isEnabled,
                key: matchingItem.key,
              }
            : { ...item, isEnabled: true };
        })
      : additionalColumns;

    const reorderedColumns = isReorderable
      ? columns.map((propColumn) => {
          const matchingColumn = columnsWithWidth.find(
            (col) => col.dataIndex === propColumn.dataIndex
          );
          return matchingColumn || propColumn;
        })
      : columnsWithWidth;

    return reorderedColumns.filter((column) => column != null);
  }, [
    showPanel,
    copyToClipboard,
    currentDashboard,
    permissions,
    isResizable,
    isReorderable,
    columns,
    showSiteColumn,
    openActionRow,
    width,
    renderFormatButtonGroup,
    handleFavouriteToggle,
    showAllRevisions,
    renderDescriptions,
    showProfilePanel,
    showCommentPanel,
  ]);

  const tableView = useMemo(() => ".large", []);

  return (
    <>
      {contextHolder}

      <div className="dashboard-table-with-settings">
        <CommonTable
          identifier="documentFile.documentFileId"
          rowKey={(record) => record.documentFile.documentFileId}
          showTotalCount={showTotalCount}
          columns={buildColumns}
          checkboxes={true}
          id={id}
          refetch={refetch}
          fetchAll={fetchAll}
          tableData={tableData}
          tableCount={tableCount}
          paginationPos="top"
          enablePageSizeIncrease={false}
          pageSize={pageSize}
          currentPage={currentPage}
          isResizable={isResizable}
          isReorderable={isReorderable}
          tableView={tableView}
          resetSearch={resetSearch}
          getTableHeight={getTableHeight}
          customEmpty={emptyTableMessage}
          isLoading={isLoading}
          virtual={true}
          nativeHorizontalScroll={true}
          width={width}
          formatColumnHoverMode={false}
        />
        {currentDashboard === 'documentDashboard' && (
          <Dropdown
            menu={{ items: columnMenuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <span
              className="reset-columns-trigger"
              role="button"
              tabIndex={0}
              aria-label="Column settings"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }}
            >
              <i className="ri-settings-2-line" aria-hidden="true" />
            </span>
          </Dropdown>
        )}
      </div>
    </>
  );
};

const NO_ITEMS = [];

const DashboardTable = (props) => {
  const { id } = props;
  const dispatch = useDispatch();

  const sharedFilters = useSelector((state) =>
    get(state.shared, `${id}.filters`, NO_ITEMS)
  );
  const pageSize = useSelector((state) =>
    get(state.shared, `${id}.filters.pageSize`)
  );
  const currentPage = useSelector((state) =>
    get(state.shared, `${id}.filters.currentPage`)
  );
  const permissions = useSelector((state) => state.userDetails.permissions);
  const showAllRevisions = useSelector((state) =>
    get(state.shared, `${id}.filters.documentStatusId`, false)
  );

  const handleShowPanel = useCallback(
    (item) => {
      dispatch(
        setCurrentItem({
          documentNumber: item.documentNumber,
          documentId: item.documentId,
        })
      );
      dispatch(showPanel(panels.dashboard));
    },
    [dispatch]
  );

  const handleShowCommentPanel = useCallback(
    (value) => {
      dispatch(setValue({ key: `${panels.showChangeComment}.item`, value }));
      dispatch(showPanel(panels.showChangeComment));
    },
    [dispatch]
  );

  const handleShowProfilePanel = useCallback(
    (item) => {
      dispatch(clearContactDetails());
      if (item) dispatch(getDetailsByUserId(item));
      dispatch(showPanel(panels.siteUser));
    },
    [dispatch]
  );

  const handleShowPreviewPanel = useCallback(
    (item) => {
      dispatch(
        setValue({
          key: `${panels.pdfPreview}.item`,
          value: { ...item, ...item.documentFile },
        })
      );
      dispatch(
        dashboardApi.endpoints.downloadPDFBlob.initiate({
          documentFileIds: [item.documentFile.documentFileId],
          documentNumber: item.documentNumber,
        })
      );
      dispatch(showPanel(panels.pdfPreview));
    },
    [dispatch]
  );

  return (
    <DashboardTableView
      {...props}
      sharedFilters={sharedFilters}
      pageSize={pageSize}
      currentPage={currentPage}
      permissions={permissions}
      showAllRevisions={showAllRevisions}
      showPanel={handleShowPanel}
      showCommentPanel={handleShowCommentPanel}
      showProfilePanel={handleShowProfilePanel}
      showPreviewPanel={handleShowPreviewPanel}
    />
  );
};

export default DashboardTable;
